import { PromiseCompletionSource } from "scene-manager";
import { IDestroyOptions, isMobile, Sprite } from "pixi.js";
import { ICanvasScaler } from "../../../../canvas/ICanvasScaler";
import { MenuResourcesPackage } from "../resources/MenuResourcesPackage";
import { MenuController } from "../controllers/MenuController";
import { AdaptiveView } from "../../../common/views/AdaptiveView";
import { CoinsCounter } from "../../../common/components/CoinsCounter";
import { LevelView } from "../components/LevelView";
import { CommonResourcesPackage } from "../../../common/resources/CommonResourcesPackage";
import { LocalizationModel } from "../../../common/models/LocalizationModel";
import { PlayerModel } from "../../../common/models/PlayerModel";
import { FullScreenBackground } from "../../../common/components/FullScreenBackground";
import { SettingsModel } from "../../../common/models/SettingsModel";
import { AudioController, Music } from "../../game/components/AudioController";
import { UIButtonBase } from "../../../common/components/UIButtonBase";
import { SpineResourcesPackage } from "../../../common/resources/SpineResourcesPackage";
import { BackgroundResourcesPackage } from "../../../common/resources/BackgroundResourcesPackage";

export class MenuView extends AdaptiveView<void> {
    private background: FullScreenBackground;
    private logo: Sprite;
    private settingsButton: UIButtonBase;
    private coinsCounter: CoinsCounter;
    private currentLevel: LevelView;
    private playButton: UIButtonBase;

    public constructor(
        private readonly backgroundResources: BackgroundResourcesPackage,
        private readonly commonResources: CommonResourcesPackage,
        private readonly menuResources: MenuResourcesPackage,
        private readonly spineResources: SpineResourcesPackage,
        private readonly settingsModel: SettingsModel,
        private readonly localizationModel: LocalizationModel,
        private readonly playerModel: PlayerModel,
        private readonly menuController: MenuController,
        private readonly audioController: AudioController,
        canvasScaler: ICanvasScaler,
        operationControls: PromiseCompletionSource<void>
    ) {
        super(canvasScaler, operationControls);
        this.build();
        this.layout(this.canvasScaler.referenceWidth, this.canvasScaler.referenceHeight);

        this.handleOrientationChange = this.handleOrientationChange.bind(this);
        window.addEventListener("orientationchange", this.handleOrientationChange);

        this.settingsButton.onUp.add(this.onSettingsButtonClick, this);
        this.playButton.onUp.add(this.onPlayButtonClick, this);
    }

    public override destroy(options?: IDestroyOptions | boolean): void {
        window.removeEventListener("orientationchange", this.handleOrientationChange);
        this.settingsButton.onUp.remove(this.onSettingsButtonClick, this);
        this.playButton.onUp.remove(this.onPlayButtonClick, this);
        super.destroy(options);
    }

    public async start(): Promise<void> {
        this.audioController.playMusic(Music.MenuBackground);
        this.handleOrientationChange();
    }

    private build(): void {
        this.cursor = "hover";
        this.background = new FullScreenBackground(this.canvasScaler, this.backgroundResources.assets_backgrounds_background_png);
        this.logo = new Sprite(this.backgroundResources.assets_backgrounds_logo_png);
        this.settingsButton = new UIButtonBase({ defaultTexture: this.menuResources.assets_menu_settings_button_png });
        this.coinsCounter = new CoinsCounter(this.playerModel, this.commonResources, this.spineResources);
        this.currentLevel = new LevelView(this.settingsModel, this.localizationModel, this.playerModel, this.commonResources);
        this.playButton = new UIButtonBase({ defaultTexture: this.menuResources.assets_menu_play_button_png });

        this.addChild(
            this.background,
            this.logo,
            this.settingsButton,
            this.coinsCounter,
            this.playButton,
            this.currentLevel
        );
    }

    protected layout(width: number, height: number): void {
        switch (height) {
            case 2400:
                this.layout9x20(width);
                break;
            case 1620:
                this.layout10x9(width);
                break;
            case 1800:
                this.layout4x3(width);
                break;
        }
    }

    private layout9x20(width: number): void {
        this.logo.position.set((this.canvasScaler.referenceWidth - this.logo.width) * 0.5, 450);
        this.settingsButton.scale.set(0.85);
        this.settingsButton.position.set(900, 200);
        this.coinsCounter.scale.set(0.85);
        this.coinsCounter.position.set(728.725 + this.coinsCounter.width * 0.5, 60 + this.coinsCounter.height * 0.5);
        this.currentLevel.position.set((this.canvasScaler.referenceWidth - this.currentLevel.width) * 0.5, 40);
        this.playButton.position.set((this.canvasScaler.referenceWidth - this.playButton.width) * 0.5, 1650);
    }

    private layout10x9(width: number): void {
        this.logo.position.set((this.canvasScaler.referenceWidth - this.logo.width) * 0.5, 300);
        this.settingsButton.position.set(width - this.settingsButton.width - 185, 100);
        this.coinsCounter.position.set(150 + this.coinsCounter.width * 0.5, 100 + this.coinsCounter.height * 0.5);
        this.currentLevel.position.set((this.canvasScaler.referenceWidth - this.currentLevel.width) * 0.5, 100);
        this.playButton.position.set((this.canvasScaler.referenceWidth - this.playButton.width) * 0.5, 1100);
    }

    private layout4x3(width: number): void {
        this.logo.position.set((this.canvasScaler.referenceWidth - this.logo.width) * 0.5, 350);
        this.settingsButton.position.set(width - this.settingsButton.width - 185, 100);
        this.coinsCounter.position.set(150 + this.coinsCounter.width * 0.5, 100 + this.coinsCounter.height * 0.5);
        this.currentLevel.position.set((this.canvasScaler.referenceWidth - this.currentLevel.width) * 0.5, 100);
        this.playButton.position.set((this.canvasScaler.referenceWidth - this.playButton.width) * 0.5, 1100);
    }

    private handleOrientationChange(): void {
        requestAnimationFrame(() => {
            isMobile.phone && !window.isPortrait() && this.menuController.showForceRotatePopup();
        });
    }

    private async onSettingsButtonClick(): Promise<void> {
        await this.menuController.showSettings();
    }

    private async onPlayButtonClick(): Promise<void> {
        this.playButton.enabled = false;
        await this.menuController.showGame(this.operationControls);
        this.playButton.enabled = true;
    }
}
